import { IconChevronDown, IconChevronLeft, IconChevronRight, IconChevronsLeft, IconChevronsRight, IconChevronUp } from '@tabler/icons-react'
import {
  useId, useRef, useState, type HTMLAttributes, type ReactNode, type TableHTMLAttributes, type ThHTMLAttributes,
} from 'react'
import { NavLink, type NavLinkProps } from 'react-router-dom'
import { fileKindOf, fileKinds, monogram, projectPalette, type FileKindKey } from '../../../packages/shared/src/tokens'
import { type Tone } from './Feedback'
import { cx } from './primitives'

/* ------------------------------------------------------------- rozetler */

export type BadgeTone = Tone | 'ink' | 'lime' | 'outline' | 'danger-solid'

export function Badge({
  tone = 'neutral', size = 'md', square, dot, icon, className, style, bg, fg, children, ...rest
}: HTMLAttributes<HTMLSpanElement> & {
  tone?: BadgeTone; size?: 'sm' | 'md'; square?: boolean; dot?: string; icon?: ReactNode; bg?: string; fg?: string
}) {
  return (
    <span className={cx('ui-badge', `ui-badge--${tone}`, size === 'sm' && 'ui-badge--sm', square && 'ui-badge--square', className)}
      style={bg || fg ? { background: bg, color: fg, ...style } : style} {...rest}>
      {dot && <span className="ui-badge__dot" style={{ background: dot }} aria-hidden="true" />}
      {icon}{children}
    </span>
  )
}

export function Count({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cx('ui-count', className)}>{children}</span>
}

/* ------------------------------------------------------- avatar, monogram */

/** Kişi adından kararlı renk (aynı kişi her yerde aynı renk). */
export function personColor(seed: string): string {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0
  return projectPalette[Math.abs(h) % projectPalette.length]
}

export function initialsOf(name: string): string {
  return monogram(name || '?')
}

export function Avatar({
  name, size = 32, color, square, dark, title, className,
}: { name: string; size?: number; color?: string; square?: boolean; dark?: boolean; title?: string; className?: string }) {
  return (
    <span className={cx('ui-avatar', square && 'ui-avatar--square', className)} title={title}
      aria-hidden={title ? undefined : true}
      style={{
        width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.36)),
        background: dark ? 'var(--nx-ink-tile)' : (color ?? personColor(name)), color: dark ? 'var(--nx-lime)' : '#ffffff',
      }}>
      {initialsOf(name)}
    </span>
  )
}

export function AvatarStack({
  people, max = 3, size = 24,
}: { people: Array<{ id: string; name: string; square?: boolean; title?: string }>; max?: number; size?: number }) {
  const shown = people.slice(0, max)
  const more = people.length - shown.length
  return (
    <span className="ui-avatar-stack">
      {shown.map((p) => <Avatar key={p.id} name={p.name} size={size} square={p.square} title={p.title ?? p.name} />)}
      {more > 0 && (
        <span className="ui-avatar" style={{ width: size, height: size, fontSize: Math.round(size * 0.38), background: 'var(--nx-fg-faint)' }} title={`${more} kişi daha`}>+{more}</span>
      )}
    </span>
  )
}

/** Proje / ekip monogramı (yuvarlatılmış kare). */
export function Monogram({
  name, color, size = 40, radius, dark, className,
}: { name: string; color?: string; size?: number; radius?: number; dark?: boolean; className?: string }) {
  return (
    <span aria-hidden="true" className={className}
      style={{
        width: size, height: size, borderRadius: radius ?? Math.round(size * 0.28), flexShrink: 0,
        background: dark ? 'var(--nx-ink)' : (color ?? personColor(name)), color: dark ? 'var(--nx-lime)' : '#ffffff',
        fontSize: Math.round(size * 0.34), fontWeight: 600, letterSpacing: '-0.02em',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>
      {initialsOf(name)}
    </span>
  )
}

/**
 * Dosya türü karosu: türün rengi üzerinde kısa adı (W, X, P, PDF…). Renk türü anlatır;
 * erişilebilir ad, karonun yanında duran dosya adından gelir.
 */
export function FileTile({
  kind, name, size = 40, radius, className,
}: { kind?: FileKindKey | null; name?: string; size?: number; radius?: number; className?: string }) {
  // HTML sunucuda "Diğer" türündedir ama uygulama içinde önizlenir; kendi karosu var.
  const html = /\.x?html?$/i.test(name ?? '')
  const k = html && (!kind || kind === 'OTHER') ? { short: 'HTML', color: '#C8501C' } : fileKinds[kind ?? fileKindOf(name ?? '')]
  const long = k.short.length > 1
  return (
    <span aria-hidden="true" className={className}
      style={{
        width: size, height: size, borderRadius: radius ?? Math.round(size * 0.26), flexShrink: 0, background: k.color, color: '#ffffff',
        fontSize: Math.round(size * (long ? 0.26 : 0.34)), fontWeight: 700, letterSpacing: long ? '0.02em' : 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>
      {k.short}
    </span>
  )
}

/* ---------------------------------------------------------------- panel */

export function Panel({
  pad, dark, className, ...rest
}: HTMLAttributes<HTMLDivElement> & { pad?: boolean | number; dark?: boolean }) {
  const style = typeof pad === 'number' ? { padding: pad, ...rest.style } : rest.style
  return <div {...rest} style={style} className={cx('ui-panel', pad === true && 'ui-panel--pad', dark && 'ui-panel--dark', className)} />
}

/* ---------------------------------------------------------------- tablo */

export function Table({ hover = true, className, wrapClassName, ...rest }: TableHTMLAttributes<HTMLTableElement> & { hover?: boolean; wrapClassName?: string }) {
  return (
    <div className={cx('ui-table-wrap', wrapClassName)}>
      <table className={cx('ui-table', hover && 'ui-table--hover', className)} {...rest} />
    </div>
  )
}

/** Sıralanabilir başlık hücresi: aria-sort + düğme. */
export function SortTh({
  label, active, dir, onSort, ...rest
}: ThHTMLAttributes<HTMLTableCellElement> & { label: ReactNode; active: boolean; dir: 'asc' | 'desc'; onSort: () => void }) {
  return (
    <th aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined} {...rest}>
      <button type="button" className="ui-table__sort" onClick={onSort}>
        {label}{active && (dir === 'asc' ? <IconChevronUp size={13} /> : <IconChevronDown size={13} />)}
      </button>
    </th>
  )
}

/* ------------------------------------------------------- zaman çizelgesi */

export function Timeline({ children, className }: { children: ReactNode; className?: string }) {
  return <ol className={cx('ui-timeline', className)} style={{ listStyle: 'none', margin: 0, padding: 0 }}>{children}</ol>
}

export function TimelineItem({
  bullet, bulletBg, bulletFg, children, className,
}: { bullet: ReactNode; bulletBg?: string; bulletFg?: string; children: ReactNode; className?: string }) {
  return (
    <li className={cx('ui-timeline__item', className)}>
      <span className="ui-timeline__bullet" style={{ background: bulletBg ?? 'var(--nx-neutral-bg)', color: bulletFg ?? 'var(--nx-neutral-fg)' }} aria-hidden="true">{bullet}</span>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </li>
  )
}

/* ---------------------------------------------------------------- sekmeler */

export interface TabDef<T extends string> { value: T; label: ReactNode; icon?: ReactNode; count?: ReactNode }

/** Denetimli sekmeler (role=tablist). Paneller çağıran tarafta `tabPanelProps` ile bağlanır. */
export function Tabs<T extends string>({
  value, onChange, tabs, 'aria-label': ariaLabel, variant = 'line', idBase,
}: { value: T; onChange: (v: T) => void; tabs: TabDef<T>[]; 'aria-label'?: string; variant?: 'line' | 'pill'; idBase?: string }) {
  const auto = useId()
  const base = idBase ?? auto
  const refs = useRef<Array<HTMLButtonElement | null>>([])
  const move = (i: number) => { const n = (i + tabs.length) % tabs.length; refs.current[n]?.focus(); onChange(tabs[n].value) }
  return (
    <div role="tablist" aria-label={ariaLabel} className={variant === 'line' ? 'ui-tabs' : 'ui-pill-tabs'}>
      {tabs.map((t, i) => {
        const on = t.value === value
        return (
          <button key={t.value} ref={(el) => { refs.current[i] = el }} type="button" role="tab" id={`${base}-tab-${t.value}`}
            aria-selected={on} aria-controls={`${base}-panel-${t.value}`} tabIndex={on ? 0 : -1}
            className={variant === 'line' ? 'ui-tab' : 'ui-pill-tab'} onClick={() => onChange(t.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') { e.preventDefault(); move(i + 1) }
              else if (e.key === 'ArrowLeft') { e.preventDefault(); move(i - 1) }
            }}>
            {t.icon}{t.label}
            {t.count !== undefined && t.count !== null && t.count !== '' && <span className="ui-tab__count">{t.count}</span>}
          </button>
        )
      })}
    </div>
  )
}

export function tabPanelProps(idBase: string, value: string) {
  return { role: 'tabpanel', id: `${idBase}-panel-${value}`, 'aria-labelledby': `${idBase}-tab-${value}` } as const
}

/** Sayfa sekmeleri: rota bağlantıları, etkin olan aria-current="page". */
export function NavTab({ icon, count, children, ...rest }: NavLinkProps & { icon?: ReactNode; count?: ReactNode; children: ReactNode }) {
  return (
    <NavLink {...rest} className="ui-tab">
      {icon}{children}
      {count !== undefined && count !== null && count !== '' && <span className="ui-tab__count">{count}</span>}
    </NavLink>
  )
}

/* ------------------------------------------------- sayfalama, gezinme yolu */

export function Pagination({ page, total, onChange, withEdges }: { page: number; total: number; onChange: (p: number) => void; withEdges?: boolean }) {
  if (total <= 1) return null
  const pages: Array<number | '…'> = []
  for (let p = 1; p <= total; p++) {
    if (p === 1 || p === total || Math.abs(p - page) <= 1) pages.push(p)
    else if (pages[pages.length - 1] !== '…') pages.push('…')
  }
  return (
    <nav aria-label="Sayfalar" className="ui-pagination">
      {withEdges && <button type="button" className="ui-page-btn" aria-label="İlk sayfa" disabled={page <= 1} onClick={() => onChange(1)}><IconChevronsLeft size={15} /></button>}
      <button type="button" className="ui-page-btn" aria-label="Önceki sayfa" disabled={page <= 1} onClick={() => onChange(page - 1)}><IconChevronLeft size={15} /></button>
      {pages.map((p, i) => p === '…'
        ? <span key={`e${i}`} style={{ padding: '0 4px', color: 'var(--nx-fg-faint)' }}>…</span>
        : <button key={p} type="button" className="ui-page-btn" aria-current={p === page ? 'page' : undefined} onClick={() => onChange(p)}>{p}</button>)}
      <button type="button" className="ui-page-btn" aria-label="Sonraki sayfa" disabled={page >= total} onClick={() => onChange(page + 1)}><IconChevronRight size={15} /></button>
      {withEdges && <button type="button" className="ui-page-btn" aria-label="Son sayfa" disabled={page >= total} onClick={() => onChange(total)}><IconChevronsRight size={15} /></button>}
    </nav>
  )
}

export function Breadcrumbs({ children, label = 'Konum' }: { children: ReactNode; label?: string }) {
  return <nav aria-label={label} className="ui-crumbs">{children}</nav>
}
export function CrumbSep() {
  return <span aria-hidden="true">/</span>
}

/* ------------------------------------------------------------ bırakma alanı */

export interface DropzoneProps {
  onDrop: (files: File[]) => void
  onReject?: (files: File[]) => void
  maxSize?: number
  multiple?: boolean
  accept?: string
  disabled?: boolean
  inputProps?: Record<string, string>
  children: (state: { active: boolean; reject: boolean }) => ReactNode
  className?: string
}

/**
 * Bırakılan öğelerden dosyaları toplar; klasörleri (webkitGetAsEntry) içine girerek gezer.
 * Her dosyaya, bırakılan kökten itibaren göreli yol `path` olarak yazılır ("/Klasör/alt/a.pdf");
 * tek dosyada "./a.pdf". Çağıran taraf bu yolla klasör yapısını yeniden kurabilir.
 */
async function filesFromDrop(dt: DataTransfer): Promise<File[]> {
  type Entry = { isFile: boolean; isDirectory: boolean; name: string; fullPath: string
    file?: (ok: (f: File) => void, err: (e: unknown) => void) => void
    createReader?: () => { readEntries: (ok: (e: Entry[]) => void, err: (e: unknown) => void) => void } }
  const entries = Array.from(dt.items ?? [])
    .map((i) => (i.kind === 'file' ? (i as DataTransferItem & { webkitGetAsEntry?: () => Entry | null }).webkitGetAsEntry?.() : null))
    .filter(Boolean) as Entry[]
  if (!entries.length) return Array.from(dt.files)
  const out: File[] = []
  const withPath = (f: File, path: string) => { Object.defineProperty(f, 'path', { value: path, configurable: true }); return f }
  const walk = async (e: Entry): Promise<void> => {
    if (e.isFile && e.file) {
      const f = await new Promise<File>((ok, err) => e.file!(ok, err))
      out.push(withPath(f, e.fullPath.split('/').length > 2 ? e.fullPath : `./${f.name}`))
    } else if (e.isDirectory && e.createReader) {
      const reader = e.createReader()
      // readEntries her çağrıda en fazla ~100 öğe verir; boş dönene kadar okunur.
      for (;;) {
        const batch = await new Promise<Entry[]>((ok, err) => reader.readEntries(ok, err))
        if (!batch.length) break
        for (const c of batch) await walk(c)
      }
    }
  }
  for (const e of entries) await walk(e)
  return out
}

/** Sürükle-bırak + tıklayıp seçme. Gizli dosya girişinin erişilebilir adı `inputProps`'tan. */
export function Dropzone({ onDrop, onReject, maxSize, multiple = true, accept, disabled, inputProps, children, className }: DropzoneProps) {
  const input = useRef<HTMLInputElement>(null)
  const depth = useRef(0)
  const [active, setActive] = useState(false)
  const [reject, setReject] = useState(false)
  const handle = (files: File[]) => {
    if (!files.length) return
    const ok = maxSize ? files.filter((f) => f.size <= maxSize) : files
    const bad = maxSize ? files.filter((f) => f.size > maxSize) : []
    if (bad.length) {
      onReject?.(bad)
      setReject(true)
      setTimeout(() => setReject(false), 1500)
    }
    if (ok.length) onDrop(multiple ? ok : ok.slice(0, 1))
  }
  return (
    <>
      <button type="button" className={cx('ui-dropzone', className)} data-active={active} data-reject={reject} disabled={disabled}
        onClick={() => input.current?.click()}
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = disabled ? 'none' : 'copy' }}
        // Alt öğelere geçerken dragleave/dragenter çifti gelir; sayaç titremeyi önler.
        onDragEnter={(e) => { e.preventDefault(); depth.current++; if (!disabled) setActive(true) }}
        onDragLeave={() => { depth.current = Math.max(0, depth.current - 1); if (!depth.current) setActive(false) }}
        onDrop={async (e) => {
          e.preventDefault()
          depth.current = 0
          setActive(false)
          if (disabled) return
          handle(await filesFromDrop(e.dataTransfer))
        }}>
        {children({ active, reject })}
      </button>
      <input ref={input} type="file" hidden multiple={multiple} accept={accept} disabled={disabled} {...inputProps}
        onChange={(e) => { handle(Array.from(e.currentTarget.files ?? [])); e.currentTarget.value = '' }} />
    </>
  )
}
